import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Typography,
  styled,
} from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { MdCheck, MdCloudUpload, MdRestartAlt } from "react-icons/md";
import { read, utils } from "xlsx";

import {
  EXCEL_HEADERS,
  ORDER_SOURCES,
  STATUS_CODES,
} from "constants/index";
import { ENTITY } from "constants/entities";
import {
  ASSOCIATED_CLIENTS_URL,
  ORDERS_IMPORT_PREVIEW_URL,
  ORDERS_IMPORT_URL,
} from "constants/urls";
import {
  normalizeCellString,
  toDateOnly,
  toDateString,
  toIsoDateTime,
  toNumber,
} from "helpers";
import {
  buildImportResultMessage,
  getImportReference,
} from "helpers/fileImports";
import {
  getArticleLocationSuggestionErrorMessage,
  isArticleLocationSuggestionError,
} from "helpers/articleLocationSuggestions";
import {
  findClientByCode,
  findScopedArticleByClientId,
} from "helpers/clientScope";
import {
  buildAssociatedClientCreatePayload,
  buildAssociatedClientLookupMap,
  buildAssociatedClientMatchItems,
  canCreateAssociatedClients,
  findAssociatedClientInLookupMap,
  getAssociatedClientLookupKey,
  normalizeAssociatedClientName,
} from "helpers/associatedClients";
import { buildOrderImportHeaderData } from "helpers/orders";
import { validateSchema } from "helpers/schemaValidator";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import {
  AdditionalItem,
  ArticleLocationMatchSuggestion,
  ArticleLocationSuggestionError,
  Article,
  AssociatedClient,
  AssociatedClientDecision,
  AssociatedClientMatchItem,
  Client,
  CreateOrderPayload,
  ExcelCellProps,
  ImportOrdersPartialResult,
  ImportOrdersPayload,
  ImportOrdersPreviewPayload,
  Location,
  Order,
  OrderImportPreviewSuggestion,
  Store,
} from "types";
import AssociatedClientMatchModal from "components/AssociatedClients/AssociatedClientMatchModal";
import ArticleLocationSuggestionPanel from "components/FileImport/ArticleLocationSuggestionPanel";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const inputMimeType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";

type Props = {
  clients: Client[];
  associatedClients: AssociatedClient[];
  additionalItems: AdditionalItem[];
  articles: Article[];
  locations: Location[];
  stores: Store[];
  onImported?: (orders: Order[]) => void;
};

type WorkbookRow = Record<string, unknown>;

type ImportPreview = {
  payload: ImportOrdersPayload;
  reservationPreview: ImportOrdersPreviewPayload;
  totalArticles: number;
  orderMeta: {
    clientId: number;
    clientCode: string;
    clientDocument: string;
    associatedClientName: string | null;
  }[];
};

const buildOrderGroupingKey = (clientCode: string, clientDocument: string) =>
  `${clientCode.trim().toLowerCase()}|${clientDocument.trim().toLowerCase()}`;

const groupPreviewSuggestionsByLocation = (
  suggestions: OrderImportPreviewSuggestion[],
) => {
  const groupedSuggestions = new Map<string, OrderImportPreviewSuggestion>();

  suggestions.forEach((suggestion) => {
    const key = `${suggestion.location_id}|${suggestion.amount_per_container}`;
    const previousSuggestion = groupedSuggestions.get(key);

    if (!previousSuggestion) {
      groupedSuggestions.set(key, { ...suggestion });
      return;
    }

    groupedSuggestions.set(key, {
      ...previousSuggestion,
      containers_to_prepare:
        previousSuggestion.containers_to_prepare + suggestion.containers_to_prepare,
      units_to_prepare:
        previousSuggestion.units_to_prepare + suggestion.units_to_prepare,
    });
  });

  return Array.from(groupedSuggestions.values());
};

const getOrderImportReference = (
  order: CreateOrderPayload,
  index: number,
) =>
  getImportReference(order.numero_remito) ??
  getImportReference(order.client_document) ??
  `pedido ${index + 1}`;

export default function FileImportOrders({
  clients,
  associatedClients,
  articles,
  onImported,
}: Props) {
  const { userData, addSessionData } = useSession();
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [listOfErrors, setListOfErrors] = useState<
    Array<string | ArticleLocationSuggestionError>
  >([]);
  const [resetInput, setResetInput] = useState(Math.random().toString(36));
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [pendingPreview, setPendingPreview] = useState<ImportPreview | null>(null);
  const [failedPreview, setFailedPreview] = useState<ImportPreview | null>(null);
  const [matchItems, setMatchItems] = useState<AssociatedClientMatchItem[]>([]);
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const fileUploadRef = useRef<HTMLInputElement | null>(null);
  const skippedOrderReferencesRef = useRef<string[]>([]);
  const isSubmittingImportRef = useRef(false);
  const { showSnackBar } = useToast();
  const { createData: previewOrdersImport } = useCrud<ImportOrdersPayload>(
    ORDERS_IMPORT_PREVIEW_URL,
    {
      disableAutoFetch: true,
    },
  );
  const { createData: importOrders } = useCrud<ImportOrdersPayload>(
    ORDERS_IMPORT_URL,
    {
      disableAutoFetch: true,
    },
  );
  const { createData: createAssociatedClient } = useCrud(
    ASSOCIATED_CLIENTS_URL,
    {
      disableAutoFetch: true,
    },
  );

  const associatedClientLookupMap = useMemo(
    () => buildAssociatedClientLookupMap(associatedClients),
    [associatedClients],
  );

  const closeErrorModal = () => {
    setErrorOpen(false);
    setListOfErrors([]);
    skippedOrderReferencesRef.current = [];
    setResetInput(Math.random().toString(36));
  };

  const openErrors = (errors: Array<string | ArticleLocationSuggestionError>) => {
    setPreviewOpen(false);
    setMatchModalOpen(false);
    setPreview(null);
    setPendingPreview(null);
    setFailedPreview(null);
    setMatchItems([]);
    setListOfErrors(errors);
    setErrorOpen(true);
    setLoading(false);
  };

  const normalizeBackendErrors = (
    errors:
      | Array<string | { message?: string; msg?: string }>
      | undefined,
  ): Array<string | ArticleLocationSuggestionError> =>
    errors
      ?.map((error) => {
        if (typeof error === "string") return error;
        if (isArticleLocationSuggestionError(error)) return error;
        return error.message || error.msg || "Error de validacion.";
      })
      .filter(Boolean) ?? [];

  const openBackendErrors = (
    errors: Array<string | ArticleLocationSuggestionError>,
    recoverablePreview?: ImportPreview | null,
  ) => {
    setPreviewOpen(false);
    setMatchModalOpen(false);
    setPreview(null);
    setPendingPreview(null);
    setFailedPreview(recoverablePreview ?? null);
    setMatchItems([]);
    setListOfErrors(errors);
    setErrorOpen(true);
    setLoading(false);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreview(null);
    setResetInput(Math.random().toString(36));
  };

  const readWorkbookRows = (file: File): Promise<WorkbookRow[]> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onerror = () => reject(new Error("No se pudo leer el archivo."));

      reader.onload = (ev) => {
        try {
          const workbook = read(
            (ev.target as FileReader).result as ArrayBuffer,
            { type: "array" },
          );
          const firstSheetName = workbook.SheetNames[0];

          if (!firstSheetName) {
            throw new Error("El archivo no contiene hojas.");
          }

          resolve(
            utils.sheet_to_json(workbook.Sheets[firstSheetName], {
              defval: "",
              raw: false,
            }) as WorkbookRow[],
          );
        } catch (error) {
          reject(error);
        }
      };

      reader.readAsArrayBuffer(file);
    });

  const validateWorkbookRows = (rows: WorkbookRow[]) => {
    const errors: string[] = [];

    if (!rows.length) {
      errors.push("La hoja del archivo no contiene filas.");
      return errors;
    }

    const schema: Record<string, ExcelCellProps> = {
      [EXCEL_HEADERS.ORDERS_IMPORT.FECHA]: {
        type: "string",
        required: true,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.CLIENTE]: {
        type: "string",
        required: true,
        associations: clients.map((client) => client.code),
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.DOCUMENTO_DEL_CLIENTE]: {
        type: "string",
        required: true,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.CLIENTE_ASOCIADO]: {
        type: "string",
        required: false,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.OBSERVACIONES]: {
        type: "string",
        required: false,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.ARTICULO]: {
        type: "string",
        required: true,
        associations: articles.map((article) => article.code),
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.CANTIDAD]: {
        type: "number",
        required: true,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.LOTE]: {
        type: "string",
        required: false,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.ORDEN_DE_COMPRA]: {
        type: "string",
        required: false,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.FECHA_VENCIMIENTO]: {
        type: "string",
        required: false,
      },
      [EXCEL_HEADERS.ORDERS_IMPORT.VEHICULO]: {
        type: "string",
        required: false,
      },
    };

    const schemaValidation = validateSchema(rows, schema);

    if (!schemaValidation.isValid) {
      errors.push(...schemaValidation.errors.map((error) => error.message));
    }

    const groupedHeaders = new Map<
      string,
      {
        rowNumber: number;
        fecha: string;
        clientCode: string;
        associatedClientName: string;
        observation: string;
        vehicle: string;
      }
    >();

    rows.forEach((row, index) => {
      const rowNumber = index + 2;
      const clientCode =
        normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.CLIENTE]) ?? "";
      const clientDocument =
        normalizeCellString(
          row[EXCEL_HEADERS.ORDERS_IMPORT.DOCUMENTO_DEL_CLIENTE],
        ) ?? "";
      const groupingKey = buildOrderGroupingKey(clientCode, clientDocument);
      const currentHeader = {
        rowNumber,
        fecha: normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.FECHA]) ?? "",
        clientCode,
        associatedClientName:
          normalizeAssociatedClientName(
            row[EXCEL_HEADERS.ORDERS_IMPORT.CLIENTE_ASOCIADO],
          ) ?? "",
        observation:
          normalizeCellString(
            row[EXCEL_HEADERS.ORDERS_IMPORT.OBSERVACIONES],
          ) ?? "",
        vehicle:
          normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.VEHICULO]) ?? "",
      };

      if (!clientCode || !clientDocument) {
        return;
      }

      const previousHeader = groupedHeaders.get(groupingKey);

      if (!previousHeader) {
        groupedHeaders.set(groupingKey, currentHeader);
        return;
      }

      if (previousHeader.fecha !== currentHeader.fecha) {
        errors.push(
          `Las filas ${previousHeader.rowNumber} y ${rowNumber} del pedido "${clientDocument}" tienen fechas distintas.`,
        );
      }

      if (
        previousHeader.associatedClientName !== currentHeader.associatedClientName
      ) {
        errors.push(
          `Las filas ${previousHeader.rowNumber} y ${rowNumber} del pedido "${clientDocument}" tienen clientes asociados distintos.`,
        );
      }

      if (previousHeader.observation !== currentHeader.observation) {
        errors.push(
          `Las filas ${previousHeader.rowNumber} y ${rowNumber} del pedido "${clientDocument}" tienen observaciones distintas.`,
        );
      }

      if (previousHeader.vehicle !== currentHeader.vehicle) {
        errors.push(
          `Las filas ${previousHeader.rowNumber} y ${rowNumber} del pedido "${clientDocument}" tienen vehiculos distintos.`,
        );
      }
    });

    rows.forEach((row, index) => {
      const articleCode =
        normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.ARTICULO]) ?? "";
      const requestedUnits = toNumber(
        row[EXCEL_HEADERS.ORDERS_IMPORT.CANTIDAD] as string | number,
      );
      const clientCode =
        normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.CLIENTE]) ?? "";
      const client = findClientByCode(clients, clientCode);

      if (!client) {
        return;
      }

      const article = findScopedArticleByClientId(
        clients,
        articles,
        client.id,
        articleCode,
      );

      if (!article) {
        errors.push(
          `La fila ${index + 2} referencia el articulo "${articleCode}" que no existe para el cliente "${clientCode}".`,
        );
      }

      if (requestedUnits <= 0) {
        errors.push(
          `La cantidad de la fila ${index + 2} debe ser mayor a cero.`,
        );
      }
    });

    return Array.from(new Set(errors));
  };

  const buildImportPayload = (rows: WorkbookRow[]): ImportPreview => {
    const groupedOrders = new Map<
      string,
      {
        client: Client;
        firstRow: WorkbookRow;
        rows: Array<{ row: WorkbookRow; rowNumber: number }>;
      }
    >();

    rows.forEach((row, index) => {
      const rowNumber = index + 2;
      const clientCode =
        normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.CLIENTE]) ?? "";
      const clientDocument =
        normalizeCellString(
          row[EXCEL_HEADERS.ORDERS_IMPORT.DOCUMENTO_DEL_CLIENTE],
        ) ?? "";
      const client = findClientByCode(clients, clientCode);

      if (!client) {
        throw new Error(
          `No se encontro el cliente "${clientCode}" en la fila ${rowNumber}.`,
        );
      }

      const groupingKey = buildOrderGroupingKey(clientCode, clientDocument);
      const existingGroup = groupedOrders.get(groupingKey);

      if (existingGroup) {
        existingGroup.rows.push({ row, rowNumber });
        return;
      }

      groupedOrders.set(groupingKey, {
        client,
        firstRow: row,
        rows: [{ row, rowNumber }],
      });
    });

    const orderMeta: ImportPreview["orderMeta"] = [];

    const orders = Array.from(groupedOrders.values()).map((group) => {
      const clientDocument =
        normalizeCellString(
          group.firstRow[EXCEL_HEADERS.ORDERS_IMPORT.DOCUMENTO_DEL_CLIENTE],
        ) ?? "";
      const associatedClientName =
        normalizeAssociatedClientName(
          group.firstRow[EXCEL_HEADERS.ORDERS_IMPORT.CLIENTE_ASOCIADO],
        ) ?? null;
      const associatedClient = associatedClientName
        ? findAssociatedClientInLookupMap(
            associatedClientLookupMap,
            group.client.id,
            associatedClientName,
          )
        : null;

      const orderArticles = group.rows.map(({ row, rowNumber }) => {
        const articleCode =
          normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.ARTICULO]) ?? "";
        const article = findScopedArticleByClientId(
          clients,
          articles,
          group.client.id,
          articleCode,
        );

        if (!article) {
          throw new Error(
            `El articulo "${articleCode}" no existe para el cliente "${group.client.code}" en la fila ${rowNumber}.`,
          );
        }

        const requestedUnits = toNumber(
          row[EXCEL_HEADERS.ORDERS_IMPORT.CANTIDAD] as string | number,
        );
        const lote =
          normalizeCellString(row[EXCEL_HEADERS.ORDERS_IMPORT.LOTE]) ?? null;
        const oc =
          normalizeCellString(
            row[EXCEL_HEADERS.ORDERS_IMPORT.ORDEN_DE_COMPRA],
          ) ?? null;
        const fechaVencimiento = toDateOnly(
          row[EXCEL_HEADERS.ORDERS_IMPORT.FECHA_VENCIMIENTO],
        );
        return {
          article_id: article.id,
          requested_units: requestedUnits,
          lote,
          oc,
          fecha_vencimiento: fechaVencimiento,
          line_data: null,
          source_row_number: rowNumber,
        };
      });

      orderMeta.push({
        clientId: group.client.id,
        clientCode: group.client.code,
        clientDocument,
        associatedClientName,
      });

      return {
        client_id: group.client.id,
        associated_client_id: associatedClient?.id ?? null,
        client_document: clientDocument,
        numero_remito: "",
        vehicle:
          normalizeCellString(
            group.firstRow[EXCEL_HEADERS.ORDERS_IMPORT.VEHICULO],
          ) ?? null,
        received_at: toIsoDateTime(
          group.firstRow[EXCEL_HEADERS.ORDERS_IMPORT.FECHA],
          "Fecha",
        ),
        preparation_date: null,
        observation:
          normalizeCellString(
            group.firstRow[EXCEL_HEADERS.ORDERS_IMPORT.OBSERVACIONES],
          ) ?? null,
        source: ORDER_SOURCES.IMPORT,
        header_data: buildOrderImportHeaderData(group.firstRow),
        store_id: null,
        articles: orderArticles,
        additional_items: [],
      } satisfies CreateOrderPayload;
    });

    return {
      payload: { orders },
      reservationPreview: { orders: [] },
      totalArticles: orders.reduce(
        (acc, order) => acc + order.articles.length,
        0,
      ),
      orderMeta,
    };
  };

  const resetState = () => {
    setLoading(false);
    setPreviewOpen(false);
    setErrorOpen(false);
    setMatchModalOpen(false);
    setPreview(null);
    setPendingPreview(null);
    setMatchItems([]);
    setListOfErrors([]);
    skippedOrderReferencesRef.current = [];
    setResetInput(Math.random().toString(36));
  };

  const continueWithPreview = async (nextPreview: ImportPreview) => {
    try {
      setLoading(true);

      const previewResponse = await previewOrdersImport(
        nextPreview.payload,
        true,
      );

      if (previewResponse.status !== STATUS_CODES.OK) {
        const backendErrors = normalizeBackendErrors(
          previewResponse.data?.errors as
            | Array<string | ArticleLocationSuggestionError>
            | undefined,
        );

        openBackendErrors(
          backendErrors.length > 0
            ? backendErrors
            : [
                previewResponse.data?.message ||
                  "No se pudo generar la recomendacion de preparacion.",
            ],
          nextPreview,
        );

        return;
      }

      setPreview({
        ...nextPreview,
        reservationPreview:
          previewResponse.data as ImportOrdersPreviewPayload,
      });

      setPreviewOpen(true);
      setLoading(false);
    } catch (error: any) {
      openErrors([
        error?.message ||
          "No se pudo generar la vista previa de los pedidos.",
      ]);
    }
  };

  const handleConfirmAssociatedClientMatches = async (decisions: AssociatedClientDecision[]) => {
    if (!pendingPreview) return;

    const decisionMap = new Map(
      decisions.map((decision) => [decision.key, decision] as const ),
    );

    const resolvedOrders = pendingPreview.payload.orders.map(
      (order, index) => {
        const orderMeta = pendingPreview.orderMeta[index];

        const key = getAssociatedClientLookupKey(
          orderMeta.clientId,
          orderMeta.associatedClientName,
        );

        const decision = key ? decisionMap.get(key) : undefined;

        if (!decision) {
          return { ...order };
        }

        return {
          ...order,
          associated_client_id:
            decision.action === "use-existing"
              ? decision.associatedClientId
              : null,
        };
      },
    );

    const resolvedPreview: ImportPreview = {
      ...pendingPreview,
      payload: {
        orders: resolvedOrders,
      },
    };

    setMatchModalOpen(false);
    setPendingPreview(null);
    setMatchItems([]);

    await continueWithPreview(resolvedPreview);
  };

  const handleSubmitImport = async () => {
    if (!preview || isSubmittingImportRef.current) {
      return;
    }

    isSubmittingImportRef.current = true;
    setLoading(true);

    try {
      const associatedClientMap = new Map(associatedClientLookupMap);
      const resolvedPayload: ImportOrdersPayload = {
        orders: [...preview.payload.orders],
      };
      let createdAssociatedClientsCount = 0;

      for (let index = 0; index < resolvedPayload.orders.length; index += 1) {
        const order = resolvedPayload.orders[index];
        const orderMeta = preview.orderMeta[index];
        const associatedClientName = normalizeAssociatedClientName(
          orderMeta?.associatedClientName,
        );

        if (!associatedClientName) {
          order.associated_client_id = null;
          continue;
        }

        if (order.associated_client_id != null) {
          continue;
        }

        const existingAssociatedClient = findAssociatedClientInLookupMap(
          associatedClientMap,
          orderMeta.clientId,
          associatedClientName,
        );

        if (existingAssociatedClient) {
          order.associated_client_id = existingAssociatedClient.id;
          continue;
        }

        if (!canCreateAssociatedClients(userData?.role)) {
          openErrors([
            `El cliente asociado "${associatedClientName}" no existe para el cliente "${orderMeta.clientCode}" y tu rol no puede crearlo automaticamente.`,
          ]);
          return;
        }

        const response = await createAssociatedClient(
          buildAssociatedClientCreatePayload(
            orderMeta.clientId,
            associatedClientName,
          ),
          true,
        );

        if (response.status !== STATUS_CODES.OK) {
          openErrors([
            response.data?.message ||
              `No se pudo crear el cliente asociado "${associatedClientName}" para el cliente "${orderMeta.clientCode}".`,
          ]);
          return;
        }

        const createdAssociatedClient = response.data as AssociatedClient;

        addSessionData(ENTITY.ASSOCIATED_CLIENTS, createdAssociatedClient);
        createdAssociatedClientsCount += 1;

        const createdLookupKey = getAssociatedClientLookupKey(
          createdAssociatedClient.client_id,
          createdAssociatedClient.razon_social,
        );

        if (createdLookupKey) {
          associatedClientMap.set(createdLookupKey, createdAssociatedClient);
        }

        order.associated_client_id = createdAssociatedClient.id;
      }

      const response = await importOrders(resolvedPayload, true);

      if (response.status === STATUS_CODES.OK) {
        const importResult = response.data as Order[] | ImportOrdersPartialResult;
        const importedOrders = Array.isArray(importResult)
          ? importResult
          : importResult.created;
        const failedOrders = Array.isArray(importResult)
          ? []
          : importResult.failed;
        const localFailedReferences = skippedOrderReferencesRef.current;
        const failedReferences = [
          ...localFailedReferences,
          ...failedOrders.map((failure) => failure.reference),
        ].filter((reference) => reference.trim() !== "");
        const partialMessage = Array.isArray(importResult)
          ? null
          : importResult.message;

        if (failedOrders.length > 0 || localFailedReferences.length > 0) {
          const failedErrors = failedOrders.flatMap((failure) =>
            failure.errors.length > 0 ? failure.errors : [failure.message],
          );
          const localFailedMessage =
            localFailedReferences.length > 0
              ? buildImportResultMessage(
                  "pedidos",
                  importedOrders.length,
                  failedReferences,
                )
              : null;

          if (importedOrders.length > 0) {
            onImported?.(importedOrders);
            showSnackBar(
              localFailedMessage ??
                partialMessage ??
                "Algunos pedidos no se importaron.",
              "warning",
            );
          }

          openBackendErrors(
            normalizeBackendErrors(
              localFailedMessage
                ? [localFailedMessage, ...failedErrors]
                : failedErrors,
            ),
          );
          return;
        }

        showSnackBar(
          partialMessage ??
            (createdAssociatedClientsCount > 0
              ? `Se importaron ${importedOrders.length} pedidos correctamente, se ${createdAssociatedClientsCount === 1 ? "creo" : "crearon"} ${createdAssociatedClientsCount} ${createdAssociatedClientsCount === 1 ? "cliente asociado" : "clientes asociados"} y se genero la recomendacion de preparacion.`
              : `Se importaron ${importedOrders.length} pedidos correctamente.`),
          "success",
        );
        onImported?.(importedOrders);
        resetState();
        return;
      }

      const backendErrors =
        normalizeBackendErrors(
          response.data?.errors as
            | Array<string | ArticleLocationSuggestionError>
            | undefined,
        );

      openErrors(
        backendErrors.length > 0
          ? backendErrors
          : [response.data?.message || "No se pudieron importar los pedidos."],
      );
    } finally {
      isSubmittingImportRef.current = false;
    }
  };

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(true);
    setListOfErrors([]);

    try {
      const file = e.target.files?.[0];

      if (!file) {
        openErrors(["No se selecciono ningun archivo."]);
        return;
      }

      const rows = await readWorkbookRows(file);
      const validationErrors = validateWorkbookRows(rows);

      if (validationErrors.length > 0) {
        openErrors(validationErrors);
        return;
      }

      const nextPreview = buildImportPayload(rows);

      const nextMatchItems = buildAssociatedClientMatchItems(
        nextPreview.orderMeta,
        associatedClients,
      );

      if (nextMatchItems.length > 0) {
        setPendingPreview(nextPreview);
        setMatchItems(nextMatchItems);
        setMatchModalOpen(true);
        setLoading(false);
        return;
      }

      await continueWithPreview(nextPreview);
    } catch (error: any) {
      openErrors([
        `Error procesando el Excel: ${
          error?.message ?? String(error)
        }`,
      ]);
    }
  };

  const applySuggestionToFailedPreview = (
    error: ArticleLocationSuggestionError,
    suggestion: ArticleLocationMatchSuggestion,
  ) => {
    if (!failedPreview) return;

    const orderIndex = error.target?.order_index;
    const articleIndex = error.target?.article_index;

    if (orderIndex == null || articleIndex == null) {
      openErrors(["No se pudo identificar la linea a reemplazar."]);
      return;
    }

    const nextPreview: ImportPreview = {
      ...failedPreview,
      payload: {
        orders: failedPreview.payload.orders.map((order, currentOrderIndex) => {
          if (currentOrderIndex !== orderIndex) return order;

          return {
            ...order,
            articles: order.articles.map((article, currentArticleIndex) =>
              currentArticleIndex === articleIndex
                ? {
                    ...article,
                    article_location_id: suggestion.article_location_id,
                    location_id: suggestion.location_id,
                    amount_per_container: suggestion.amount_per_container,
                    lote: suggestion.lote,
                    oc: suggestion.oc,
                    fecha_ingreso: toDateString(
                      suggestion.fecha_ingreso,
                    ),
                    fecha_vencimiento: toDateString(
                      suggestion.fecha_vencimiento,
                    ),
                  }
                : article,
            ),
          };
        }),
      },
    };

    setFailedPreview(nextPreview);
    setListOfErrors((previousErrors) =>
      previousErrors.filter((currentError) => currentError !== error),
    );
  };

  const continueWithFailedPreview = async () => {
    if (!failedPreview) return;

    const unresolvedOrderIndexes = new Set(
      listOfErrors
        .filter(isArticleLocationSuggestionError)
        .map((error) => error.target?.order_index)
        .filter((orderIndex): orderIndex is number => orderIndex != null),
    );

    const nextOrders = failedPreview.payload.orders.filter(
      (_, orderIndex) => !unresolvedOrderIndexes.has(orderIndex),
    );
    const skippedReferences = failedPreview.payload.orders
      .map((order, orderIndex) =>
        unresolvedOrderIndexes.has(orderIndex)
          ? getOrderImportReference(order, orderIndex)
          : null,
      )
      .filter((reference): reference is string => reference != null);

    if (nextOrders.length === 0) {
      openErrors(["No quedan pedidos validos para importar."]);
      return;
    }

    skippedOrderReferencesRef.current = Array.from(
      new Set([...skippedOrderReferencesRef.current, ...skippedReferences]),
    );

    const nextPreview: ImportPreview = {
      ...failedPreview,
      payload: { orders: nextOrders },
      orderMeta: failedPreview.orderMeta.filter(
        (_, orderIndex) => !unresolvedOrderIndexes.has(orderIndex),
      ),
      totalArticles: nextOrders.reduce(
        (total, order) => total + order.articles.length,
        0,
      ),
    };

    setErrorOpen(false);
    setListOfErrors([]);
    await continueWithPreview(nextPreview);
  };

  return (
    <>
      <Button
        component="label"
        variant="contained"
        startIcon={<MdCloudUpload />}
        disabled={loading}
      >
        {loading ? <CircularProgress size={20} /> : "Cargar Excel"}
        <VisuallyHiddenInput
          ref={fileUploadRef}
          key={resetInput}
          type="file"
          onChange={readFile}
          accept={inputMimeType}
        />
      </Button>

      <AssociatedClientMatchModal
        open={matchModalOpen}
        items={matchItems}
        canCreate={canCreateAssociatedClients(userData?.role)}
        onCancel={resetState}
        onConfirm={handleConfirmAssociatedClientMatches}
      />

      <Modal open={previewOpen} onClose={closePreview}>
        <Paper
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            maxHeight: "90vh",
            width: "min(52rem, 92vw)",
            transform: "translate(-50%, -50%)",
            padding: "1rem",
            overflowY: "auto",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Importacion lista para confirmar</Typography>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="success">
                Se detectaron {preview?.payload.orders.length ?? 0} pedidos y{" "}
                {preview?.totalArticles ?? 0} lineas de articulos.
              </Alert>
            </Grid>

            {preview?.reservationPreview.orders.map((order, orderIndex) => (
              <Grid item xs={12} key={`order-preview-${orderIndex}`}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Cliente {preview.orderMeta[orderIndex]?.clientCode ?? "-"} - Doc. Cliente {" "}
                    {preview.orderMeta[orderIndex]?.clientDocument ?? "-"}
                  </Typography>

                  {order.articles.map((article) => (
                    <Paper
                      key={`order-preview-${orderIndex}-${article.article_id}-${article.source_row_number ?? 0}`}
                      variant="outlined"
                      sx={{ p: 1.5, mb: 1.5 }}
                    >
                      <Typography variant="body1">
                        {article.code} - {article.description}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Fila {article.source_row_number ?? "-"} - solicitado:{" "}
                        {article.requested_units}
                      </Typography>

                      {groupPreviewSuggestionsByLocation(article.suggestions).map((
                        suggestion,
                        suggestionIndex,
                      ) => (
                        <Typography
                          key={`${suggestion.location_id}-${suggestion.amount_per_container}-${suggestionIndex}`}
                          variant="body2"
                          sx={{ mt: 0.5 }}
                        >
                          Preparar {suggestion.containers_to_prepare} de ubicacion{" "}
                          {suggestion.location_code} ({suggestion.units_to_prepare}{" "}
                          unidades, {suggestion.amount_per_container} por
                          contenedor)
                        </Typography>
                      ))}
                    </Paper>
                  ))}
                </Paper>
              </Grid>
            ))}

            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={closePreview}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitImport}
                disabled={loading}
              >
                Confirmar importacion
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>

      <Modal open={errorOpen} onClose={closeErrorModal}>
        <Paper
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            maxHeight: "90vh",
            width: "min(42rem, 92vw)",
            transform: "translate(-50%, -50%)",
            padding: "1rem",
            overflowY: "auto",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Ocurrio un error</Typography>
            </Grid>
            <Grid item xs={12}>
              {listOfErrors.map((error, index) => (
                <div
                  key={`${getArticleLocationSuggestionErrorMessage(error)}-${index}`}
                >
                  <Alert severity="error" sx={{whiteSpace: "pre-line" }}>
                    {getArticleLocationSuggestionErrorMessage(error)}
                  </Alert>
                  {isArticleLocationSuggestionError(error) && (
                    <ArticleLocationSuggestionPanel
                      error={error}
                      title={`Stock sugerido para fila ${
                        error.target?.source_row_number ?? "-"
                      }`}
                      onUseSuggestion={applySuggestionToFailedPreview}
                    />
                  )}
                </div>
              ))}
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={closeErrorModal}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  if (failedPreview) {
                    void continueWithFailedPreview();
                    return;
                  }

                  closeErrorModal();
                }}
                startIcon={failedPreview ? <MdCheck /> : <MdRestartAlt />}
                variant="contained"
              >
                {failedPreview ? "Siguiente" : "Reintentar"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>

      <Backdrop
        open={loading}
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
